/** Human-friendly "time ago" for a heartbeat timestamp. Returns "—" for null. */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) return 'just now';
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

/** Absolute, locale-formatted date-time. Returns "—" for null. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

export function formatCoord(lat: number | null | undefined, lng: number | null | undefined): string {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

export function formatSpeed(speedKmh: number | null | undefined): string {
  if (speedKmh == null) return '—';
  return `${Math.round(speedKmh)} km/h`;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** Heading as a compass point plus degrees, e.g. "NE · 47°". Returns "—" for null. */
export function formatCourse(courseDegrees: number | null | undefined): string {
  if (courseDegrees == null || Number.isNaN(courseDegrees)) return '—';
  const deg = ((courseDegrees % 360) + 360) % 360;
  const point = COMPASS[Math.round(deg / 45) % 8];
  return `${point} · ${Math.round(deg)}°`;
}

/** NMEA GPS fix-quality code → human label. Returns "—" for null. */
export function formatFixQuality(fixQuality: number | null | undefined): string {
  if (fixQuality == null) return '—';
  const labels: Record<number, string> = {
    0: 'No fix',
    1: 'GPS',
    2: 'DGPS',
    3: 'PPS',
    4: 'RTK fixed',
    5: 'RTK float',
    6: 'Estimated'
  };
  return labels[fixQuality] ? `${labels[fixQuality]} (${fixQuality})` : `Quality ${fixQuality}`;
}

/** Great-circle distance in metres between two lat/lng points (haversine). */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Compact distance, e.g. "12 m" or "1.4 km". Returns "—" for null. */
export function formatDistance(meters: number | null | undefined): string {
  if (meters == null || Number.isNaN(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

/** Compact elapsed duration between two ISO timestamps, e.g. "4s", "2m 10s". */
export function formatGap(fromIso: string, toIso: string): string {
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return '—';
  const sec = Math.max(0, Math.round((to - from) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return sec % 60 ? `${min}m ${sec % 60}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

/** How long the current ad has been playing, e.g. "4m". */
export function playingFor(adStartedAt: string | null | undefined): string {
  if (!adStartedAt) return '';
  const start = new Date(adStartedAt).getTime();
  if (Number.isNaN(start)) return '';
  const sec = Math.max(0, Math.round((Date.now() - start) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}
